import { FormTypes } from '../../../components/FormGenerator';
import VenueRegistrationFormModal from '../shared/VenueRegistrationFormModal';
import Request from '../../../utils/request';

const DEFAULT_CHECK_IF_WEB_ADMIN_ENDPOINT = '/api/venueregistration/introVenueSelection/CheckIfWebAdmin';
const DEFAULT_VENUE_CASE_CREATION_ENDPOINT = '/api/venueregistration/introVenueSelection/venueCaseCreation';

const DEFAULT_ACCESS_REQUEST_SUCCESS_EYEBROW = 'Congratulations';
const DEFAULT_ACCESS_REQUEST_SUCCESS_TITLE = "You've submitted a request to create this venue";
const DEFAULT_ACCESS_REQUEST_SUCCESS_DESCRIPTION = 'This will be sent to the LTA Customer Service team to review.';
const DEFAULT_ACCESS_REQUEST_SUCCESS_BUTTON_LABEL = 'Close';
const DEFAULT_ACCESS_REQUEST_SUCCESS_CARD_TITLE = 'What happens next?';
const DEFAULT_ACCESS_REQUEST_SUCCESS_CARD_HTML = `
	<ul class="u-pl-3 u-mb-0">
		<li class="u-body1 u-text-grey-lta u-mb-2">A case has been created for the LTA Customer Service team to validate the details that you have submitted. They will try to get to your case within 72 hours.</li>
		<li class="u-body1 u-text-grey-lta u-mb-2">They may be in touch to confirm any additional details.</li>
		<li class="u-body1 u-text-grey-lta u-mb-2">You will be contacted via email should they require anything from you or when your venue is ready to be accessed.</li>
		<li class="u-body1 u-text-grey-lta u-mb-0">Once this happens you will then be able to continue with the venue registration.</li>
	</ul>
`;

class AddVenueModal extends VenueRegistrationFormModal {
	constructor() {
		super({
			modalSelector: '#js-add-venue-form-modal',
			triggerSelectors: ['.js-add-venue-trigger'],
			stepsEndpointAttr: 'add-venue-steps-endpoint',
			submitEndpointAttr: 'add-venue-submit-endpoint',
			wrapperSelector: '.p-venue-registeration-landing-multisite-with-venue',
			sectionWrapperSelector: '.p-venue-registration-landing-wrapper',
			logPrefix: 'AddVenueModal'
		});
	}

	getFormGeneratorOptions(stepsData) {
		const questionData = stepsData?.[0] || {};

		return {
			totalSteps: 1,
			formType: FormTypes.SectionForm,
			isBackButtonEnabled: true,
			backButtonHandlerFirstStep: () => this.handleBackClick(),
			exitButtonLabel: 'Exit',
			// This is a single-step form (search or manual entry) with no draft to save, so
			// the top-right "Exit" button must always close the modal and return to the landing
			// page - it should not fall back to the base class's saveExitClickHandler(), which
			// would submit the in-progress answers (e.g. triggering venueCaseCreation while in
			// manual-entry mode).
			exitButtonHandler: () => this.closeFormModal(),
			showExitWithoutSavingButton: true,
			exitWithoutSavingButtonLabel: 'Exit without saving',
			exitWithoutSavingButtonHandler: () => this.closeFormModal(),
			submitButton: {
				title: 'Confirm this venue',
				alwaysVisible: true
			},
			successScreen: {
				eyebrow: questionData.accessRequestSuccessEyebrow || DEFAULT_ACCESS_REQUEST_SUCCESS_EYEBROW,
				title: questionData.accessRequestSuccessTitle || DEFAULT_ACCESS_REQUEST_SUCCESS_TITLE,
				description: questionData.accessRequestSuccessDescription || DEFAULT_ACCESS_REQUEST_SUCCESS_DESCRIPTION,
				buttonLabel: questionData.accessRequestSuccessButtonLabel || DEFAULT_ACCESS_REQUEST_SUCCESS_BUTTON_LABEL,
				content: this.getAccessRequestSuccessContent(questionData)
			}
		};
	}

	getAccessRequestSuccessContent(questionData = {}) {
		if (questionData.accessRequestSuccessContent) {
			return questionData.accessRequestSuccessContent;
		}

		const cardTitle = questionData.accessRequestSuccessCardTitle || DEFAULT_ACCESS_REQUEST_SUCCESS_CARD_TITLE;
		const cardHtml = questionData.accessRequestSuccessCardHtml || DEFAULT_ACCESS_REQUEST_SUCCESS_CARD_HTML;

		return `
			<div class="u-bg-white u-rounded u-p-3">
				<h2 class="u-subtitle3 u-fw-bold u-mb-2">${cardTitle}</h2>
				${cardHtml}
			</div>
		`;
	}

	/**
	 * The top back button is only shown while the venue finder is in manual-entry mode (see
	 * afterFormGeneratorInit's onManualEntryModeChange wiring below), so clicking it should
	 * return the user to the "Add a venue" search view within this same step rather than
	 * closing the modal. Returning `false` tells FormGenerator to skip its usual
	 * unload()-after-back teardown (see FormGenerator.setUpForwardAndBackEvents), otherwise the
	 * search view we just rendered would be wiped out immediately after being rebuilt.
	 */
	handleBackClick() {
		const venueFinder = this.getVenueSearchQuestion()?.ref;

		if (venueFinder?.isManualEntryMode) {
			venueFinder.showSearchView();
			return false;
		}

		this.closeFormModal();
		return undefined;
	}

	getVenueSearchQuestion() {
		return this.formGenerator?.questions?.find((question) => question.questionType === 'venueRegistrationVenueSearch');
	}

	/**
	 * "Enter your venue manually" submits to venueCaseCreation; a venue matched via
	 * search submits to the configured (data-add-venue-submit-endpoint) AssociateParkSite endpoint.
	 */
	getSubmitEndpoint() {
		const venueFinder = this.getVenueSearchQuestion()?.ref;
		const isManualEntry = !!venueFinder?.isManualEntryMode;

		if (isManualEntry) {
			return this.config.venueCaseCreationEndpoint || DEFAULT_VENUE_CASE_CREATION_ENDPOINT;
		}

		return super.getSubmitEndpoint();
	}

	/**
	 * `venueCaseCreation` expects the venue object directly (no `venue` wrapper key), unlike
	 * the search-selected-venue submit endpoint which uses the default `{ venue: {...} }` shape.
	 */
	getSubmitPayload() {
		const venueFinder = this.getVenueSearchQuestion()?.ref;
		const isManualEntry = !!venueFinder?.isManualEntryMode;

		if (!isManualEntry) {
			return super.getSubmitPayload();
		}

		const { venue, ...restAnswers } = this.answerList || {};

		return {
			...venue,
			...restAnswers,
			...(this.referenceId ? { referenceId: this.referenceId } : {})
		};
	}

	afterFormGeneratorInit() {
		const venueFinder = this.getVenueSearchQuestion()?.ref;

		if (!venueFinder) {
			return;
		}

		venueFinder.data.onSubmitDisabledChange = (isDisabled) => {
			if (this.formGenerator?.nextButtonEl) {
				this.formGenerator.nextButtonEl.disabled = !!isDisabled;
			}
		};

		venueFinder.data.onSelectHandler = (selectedVenue) => this.checkIfWebAdmin(selectedVenue);

		// The venue finder toggles between search and manual-entry views within this single step,
		// so show the top back button only while the manual-entry form is visible.
		venueFinder.data.onManualEntryModeChange = (isManualEntryMode) => {
			if (!this.formGenerator?.topBackButton) {
				return;
			}

			isManualEntryMode ? this.formGenerator.topBackButton.show() : this.formGenerator.topBackButton.hide();
		};
	}

	/**
	 * Called once after a venue is selected from the search dropdown, before the
	 * blue address card is shown. Mirrors VenueRegistrationOnboarding.checkIfWebAdmin
	 * so an already-web-admin-assigned venue is flagged the same way in this flow.
	 */
	async checkIfWebAdmin(selectedVenue = {}) {
		const questionData = this.getVenueSearchQuestion() || {};
		const endpoint = questionData.onSelectApiEndpoint || DEFAULT_CHECK_IF_WEB_ADMIN_ENDPOINT;
		const paramName = questionData.onSelectApiParamName || 'OrgId';
		const paramFrom = questionData.onSelectApiParamFrom || 'id';
		const responseKey = questionData.onSelectApiResponseKey || 'response';
		const orgId = selectedVenue?.[paramFrom];

		if (!orgId) {
			return selectedVenue;
		}

		try {
			const response = await Request.get(endpoint, {
				[paramName]: orgId,
				...(questionData.onSelectApiAdditionalParams || {})
			});
			const payload = responseKey ? response?.[responseKey] : response;
			return payload ? { ...selectedVenue, ...payload } : selectedVenue;
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(`${this.config.logPrefix} – CheckIfWebAdmin error:`, error);
			return selectedVenue;
		}
	}

	/**
	 * The LTA-review success screen only applies to manually entered venues (which need
	 * validation). A venue matched via search already exists in the system, so on submit
	 * it should just close the modal and refresh the page instead of showing that screen.
	 */
	async submitProgramDetails(shouldModalClose = true) {
		const venueFinder = this.getVenueSearchQuestion()?.ref;
		const isManualEntry = !!venueFinder?.isManualEntryMode;

		if (isManualEntry) {
			return super.submitProgramDetails(shouldModalClose);
		}

		const originalSuccessScreen = this.config.successScreen;
		this.config.successScreen = undefined;

		try {
			return await super.submitProgramDetails(shouldModalClose);
		} finally {
			this.config.successScreen = originalSuccessScreen;
		}
	}
}

VenueRegistrationFormModal.boot(AddVenueModal);

export default AddVenueModal;
